import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Download, Copy, FileText, Clock, CheckCircle, Eye, EyeOff, PenTool, Plus, History } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { getAgentById } from '../../agents'
import AgentHeader from '../../components/AgentHeader'

// Component for displaying document statistics
const DocumentStats = ({ content }) => {
  const wordCount = content ? content.split(/\s+/).filter(word => word.length > 0).length : 0
  const characterCount = content ? content.length : 0
  const estimatedPages = Math.ceil(wordCount / 500)
  const estimatedReadTime = Math.ceil(wordCount / 200)

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
      <div className="bg-bg-tertiary rounded-lg p-3 sm:p-4 border border-border-secondary">
        <div className="text-lg sm:text-xl font-semibold text-text-primary">{wordCount.toLocaleString()}</div>
        <div className="text-xs sm:text-sm text-text-tertiary">Kata</div>
      </div>
      <div className="bg-bg-tertiary rounded-lg p-3 sm:p-4 border border-border-secondary">
        <div className="text-lg sm:text-xl font-semibold text-text-primary">{characterCount.toLocaleString()}</div>
        <div className="text-xs sm:text-sm text-text-tertiary">Karakter</div>
      </div>
      <div className="bg-bg-tertiary rounded-lg p-3 sm:p-4 border border-border-secondary">
        <div className="text-lg sm:text-xl font-semibold text-text-primary">{estimatedPages}</div>
        <div className="text-xs sm:text-sm text-text-tertiary">Halaman</div>
      </div>
      <div className="bg-bg-tertiary rounded-lg p-3 sm:p-4 border border-border-secondary">
        <div className="text-lg sm:text-xl font-semibold text-text-primary">{estimatedReadTime} min</div>
        <div className="text-xs sm:text-sm text-text-tertiary">Baca</div>
      </div>
    </div>
  )
}

// Component for displaying the document content
const DocumentContent = ({ title, content, isPreview }) => {
  // For preview, show 100-300 words instead of characters
  const getPreviewContent = (text) => {
    const words = text.split(/\s+/).filter(word => word.length > 0)
    if (words.length <= 100) return text
    const previewWords = words.slice(0, 200) // Show around 200 words for preview
    return previewWords.join(' ') + (words.length > 200 ? '...' : '')
  }
  
  const displayContent = isPreview ? getPreviewContent(content) : content

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-primary p-6">
      {title && (
        <h1 className="text-xl sm:text-2xl font-bold text-text-primary mb-6 pb-4 border-b border-border-secondary">
          {title}
        </h1>
      )}
      <div className="prose prose-invert max-w-none text-text-secondary leading-relaxed">
        <div className="whitespace-pre-wrap text-sm sm:text-base leading-relaxed">
          {displayContent}
        </div>
      </div>
    </div>
  )
}

const WritingResultPage = ({ 
  query, 
  title,
  content,
  onBack,
  onBackToSearch
}) => {
  const navigate = useNavigate()
  const [isPreview, setIsPreview] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)

  // Handle navigation to home with Writing Agent selected
  const handleCreateNewDocument = () => {
    const writingAgent = getAgentById('writing_agent')
    if (writingAgent) {
      navigate('/', { state: { selectedAgentId: 'writing_agent' } })
    } else {
      navigate('/')
    }
  }

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      const textToCopy = title ? `${title}\n\n${content}` : content
      await navigator.clipboard.writeText(textToCopy)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy text:', err)
    }
  }

  // Handle download as text file
  const handleDownload = () => {
    try {
      const textToDownload = title ? `${title}\n\n${content}` : content
      const blob = new Blob([textToDownload], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = title ? `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt` : 'writing_document.txt'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download file:', err)
    }
  }

  return (
    <div className={cn(componentClasses.main, "min-h-screen p-4 sm:p-6")}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <AgentHeader
            onBack={onBack}
            icon={PenTool}
            title="Writing Agent"
            query={query}
            iconColor="text-violet-500"
            iconBgColor="bg-violet-500/20"
            actions={[
              {
                onClick: () => setIsPreview(!isPreview),
                icon: isPreview ? Eye : EyeOff,
                label: isPreview ? 'Lihat Semua' : 'Preview',
                className: cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  isPreview 
                    ? "bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary" 
                    : "bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary"
                )
              },
              {
                onClick: handleCopy,
                icon: copySuccess ? CheckCircle : Copy,
                label: copySuccess ? 'Tersalin!' : 'Salin',
                className: cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  copySuccess 
                    ? "bg-green-500/20 text-green-400" 
                    : "bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary"
                )
              },
              {
                onClick: handleDownload,
                icon: Download,
                label: 'Download',
                className: "flex items-center gap-2 px-3 py-2 bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 rounded-lg text-sm font-medium transition-colors"
              }
            ]}
          />
        </div>

        {/* Document Statistics */}
        <DocumentStats content={content} />

        {/* Document Content */}
        <DocumentContent 
          title={title} 
          content={content} 
          isPreview={isPreview} 
        />

        {/* Footer Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={handleCreateNewDocument}
            className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary rounded-lg text-sm font-medium transition-colors flex-1 sm:flex-none"
          >
            <Plus className="w-4 h-4" />
            Buat Dokumen Baru
          </button>
          <button
            onClick={() => {
              // Store content in sessionStorage to avoid URL length limitations
              try {
                const writingData = {
                  title: title || '',
                  content: content || '',
                  query: query,
                  timestamp: Date.now()
                }
                
                console.log('🔄 Storing writing data for continue editing:', {
                  titleLength: (title || '').length,
                  contentLength: (content || '').length,
                  query: query
                })
                
                // Store in sessionStorage
                sessionStorage.setItem('writingContinueData', JSON.stringify(writingData))
                
                // Verify data was stored correctly
                const storedData = sessionStorage.getItem('writingContinueData')
                if (storedData) {
                  const parsedData = JSON.parse(storedData)
                  console.log('✅ Data successfully stored in sessionStorage:', {
                    titleLength: parsedData.title?.length || 0,
                    contentLength: parsedData.content?.length || 0,
                    query: parsedData.query
                  })
                }
                
                // Navigate with continue flag and query
                window.location.href = `/agent/writing_agent?continue=true&query=${encodeURIComponent(query)}`
              } catch (error) {
                console.error('❌ Error storing data in sessionStorage:', error)
                
                // Fallback to URL parameters for smaller content
                const maxUrlLength = 2000
                const urlParams = `continue=true&query=${encodeURIComponent(query)}&title=${encodeURIComponent(title || '')}&content=${encodeURIComponent(content || '')}`
                
                if (urlParams.length < maxUrlLength) {
                  console.log('⚠️ Using URL fallback for continue editing')
                  window.location.href = `/agent/writing_agent?${urlParams}`
                } else {
                  alert('Konten terlalu panjang untuk dilanjutkan. Silakan salin konten secara manual.')
                }
              }
            }}
            className="flex items-center gap-2 px-3 py-2 bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 rounded-lg text-sm font-medium transition-colors flex-1 sm:flex-none"
          >
            <PenTool className="w-4 h-4" />
            Lanjutkan Edit
          </button>
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary rounded-lg text-sm font-medium transition-colors flex-1 sm:flex-none"
          >
            <History className="w-4 h-4" />
            Kembali ke Riwayat
          </button>
        </div>
      </div>
    </div>
  )
}

WritingResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  title: PropTypes.string,
  content: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onBackToSearch: PropTypes.func.isRequired
}

WritingResultPage.defaultProps = {
  title: ''
}

export default WritingResultPage